import React from 'react';
import { useQuery } from '@tanstack/react-query';
import MapIconsService from '../../../../../services/MapIconService/mapIcon.service';
import { findBuiltInShape, isBuiltInShape } from './builtInShapes';
import { SingleSymbologyConfig } from '../../../../../types/symbology.types';

/**
 * The small Symbol-cell preview for a unique category / quantity class:
 * the uploaded icon thumbnail (resolved to a presigned URL) for image icons,
 * the built-in shape filled with the configured fill colour, or a fill/outline
 * swatch for non-point geometries.
 */
const SymbologySwatch: React.FC<{ symbology: SingleSymbologyConfig; isPointLayer: boolean; isLineLayer?: boolean }> = ({
  symbology,
  isPointLayer,
  isLineLayer = false,
}) => {
  const { fetchIconUrl } = MapIconsService();
  const iconKey = symbology.icon?.key;
  const isImageIcon = !!(isPointLayer && iconKey && !isBuiltInShape(iconKey));
  const { data: iconUrl } = useQuery(fetchIconUrl(isImageIcon ? iconKey : undefined));

  if (isImageIcon) {
    return iconUrl ? (
      <img src={iconUrl} className="lp-unique__swatch-img" alt="" />
    ) : (
      <span className="lp-unique__swatch lp-unique__swatch--img" title="Image icon" />
    );
  }
  const hasFill = symbology.fillType !== 'none';
  const hasOutline = !!symbology.outline && !!symbology.outlineType && symbology.outlineType !== 'none';
  const outlineStyle = symbology.outlineType === 'dashed' || symbology.outlineType === 'dotted' ? symbology.outlineType : 'solid';
  const shape = isPointLayer && symbology.icon ? findBuiltInShape(symbology.icon.key) : undefined;
  if (shape) {
    return (
      <svg
        viewBox="0 0 24 24"
        className="lp-unique__swatch-svg"
        style={{
          fill: hasFill ? symbology.fill : 'none',
          stroke: hasOutline ? symbology.outline : 'none',
          strokeWidth: 1.5,
          strokeDasharray: outlineStyle === 'dashed' ? '4 2' : outlineStyle === 'dotted' ? '0.5 2.5' : undefined,
          strokeLinecap: outlineStyle === 'dotted' ? 'round' : undefined,
        }}
        aria-hidden>
        <path d={shape.path} />
      </svg>
    );
  }
  // Line geometries: Fill is the line (colour + solid/dashed/dotted), Outline is
  // an optional solid buffer drawn around it.
  if (isLineLayer) {
    const lineType = symbology.fillType === 'dashed' || symbology.fillType === 'dotted' ? symbology.fillType : 'solid';
    const hasBuffer = hasOutline && (Number(symbology.outlineWeight) || 0) > 0;
    return (
      <span
        className="lp-unique__swatch-line"
        style={{
          backgroundColor: lineType === 'solid' ? symbology.fill : 'transparent',
          borderTop: lineType === 'solid' ? undefined : `4px ${lineType} ${symbology.fill}`,
          height: lineType === 'solid' ? 4 : 0,
          boxSizing: 'content-box',
          boxShadow: hasBuffer ? `0 0 0 1px ${symbology.outline}` : undefined,
        }}
      />
    );
  }
  // Polygon swatch mirrors the map symbol: fill (or none) plus the configured
  // outline colour / weight / style. Without an outline the thin neutral border
  // in CSS stays, purely for visibility on light fills.
  const outlineBorder = hasOutline
    ? { border: `${Math.min(Math.max(Number(symbology.outlineWeight) || 1, 1), 3)}px ${outlineStyle} ${symbology.outline}` }
    : undefined;
  return <span className="lp-unique__swatch" style={{ backgroundColor: hasFill ? symbology.fill : 'transparent', ...outlineBorder }} />;
};

export default SymbologySwatch;
